import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/react";
import { parseDate } from "@internationalized/date";
import Stepper from "../stepper";
import DetailsStep from "./steps/details-step";
import ParticipantsStep from "./steps/participants-step";
import ConfirmStep from "./steps/confirmation-step";
import { Button, toast } from "@heroui/react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { createMeet as createMeetApi } from "@/api/meets";
import { getCurrentDate, getPrev15MinTime, getNextAvailable15MinTimeBasedOnDuration } from "@/utils/dates";
import { buildLocationPayload } from "@/utils/meets";

const CreateMeetsComp = ({ _onResp }) => {
  const { session } = useSession();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState("details");
  const [completedSteps, setCompletedSteps] = useState([]);
  const [creating, setCreating] = useState(false);

  const [formData, setFormData] = useState(() => ({
    requestId: crypto.randomUUID(),
    recurrence: { frequency: "once", count: 4 },
    title: "",
    date: parseDate(getCurrentDate()),
    startTime: getPrev15MinTime(),
    endTime: getNextAvailable15MinTimeBasedOnDuration(1),
    location: null,
    description: "",
    participants: [],
  }));

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const steps = {
    details: { id: "details", label: "Details" },
    participants: { id: "participants", label: "Participants" },
    confirm: { id: "confirm", label: "Confirm" },
  };

  const stepsArr = Object.values(steps);

  function validateStep(stepId) {
    if (stepId === "details") {
      if (!formData.title.trim()) {
        toast("Title Missing", { variant: "danger" });
        return false;
      }
      if (!formData.location) {
        toast.danger("Location Missing");
        return false;
      }
    }
    if (stepId === "participants") {
      if (!formData.participants || formData.participants.length === 0) {
        toast.danger("Select Participants");
        return false;
      }
    }
    return true;
  }

  const goNext = () => {
    if (!validateStep(activeStep)) return;
    setCompletedSteps((prev) => (prev.includes(activeStep) ? prev : [...prev, activeStep]));
    const idx = stepsArr.findIndex((s) => s.id === activeStep);
    if (idx < stepsArr.length - 1) setActiveStep(stepsArr[idx + 1].id);
  };

  const goBack = () => {
    const idx = stepsArr.findIndex((s) => s.id === activeStep);
    if (idx === 0) navigate('/')
    if (idx > 0) setActiveStep(stepsArr[idx - 1].id);
  };

  async function onCreateClick() {
    if (!validateStep("details") || !validateStep("participants")) return;
    const token = await session.getToken();
    const params = {
      request_id: formData.requestId,
      recurrence: formData.recurrence,
      title: formData.title,
      description: formData.description,
      meeting_date: formData.date?.toString() ?? "",
      start_time: formData.startTime,
      end_time: formData.endTime,
      members: formData.participants.map((p) => p.id),
      location: buildLocationPayload(formData.location),
    };

    setCreating(true);
    createMeetApi(params, token)
      .then((_resp) => {
        if (_resp?.data?.Result === "SUCCESS") {
          _onResp?.("success", _resp.data);
          toast.success("Meets Created");
          navigate("/");
        } else {
          throw new Error(_resp?.data?.Message || "Could not create meeting");
        }
      })
      .catch((err) => toast.danger(err.response?.data?.detail || err.message || "Could not create meeting"))
      .finally(() => setCreating(false));
  }

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const renderStep = () => {
    switch (activeStep) {
      case "details":
        return <DetailsStep formData={formData} updateFormData={updateFormData} />;
      case "participants":
        return <ParticipantsStep formData={formData} updateFormData={updateFormData} />;
      case "confirm":
        return (
          <ConfirmStep
            formData={formData}
            updateFormData={updateFormData}
            onEditParticipants={() => setActiveStep("participants")}
            onEditDate={() => setActiveStep('details')}
            onEditLocation={() => setActiveStep('details')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full gap-6 overflow-hidden pt-10">
      <div className="shrink-0">
        <Stepper
          steps={stepsArr}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          completedSteps={completedSteps}
        />
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-[linear-gradient(to_top,#6b5fe000_10%,white_60%)] dark:bg-[linear-gradient(to_top,#00000040,black)] grow flex flex-col overflow-hidden rounded-t-3xl"
      >
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
          {renderStep()}
        </div>

        <div className="shrink-0 flex justify-between gap-4 px-4 pb-20 pt-4">
          <div>
            <Button type="button" onClick={goBack} className={"create-meets-back-button font-bold bg-white shadow-md text-foreground h-12.5"}>
              <ArrowLeft size={10} className="size-3.5" />
              Back
            </Button>
          </div>

          {activeStep !== "confirm" ? (
            <Button type="button" className={"shadow-md dark:shadow-none font-bold shadow-accent h-12.5"} fullWidth onClick={goNext}>
              Next
              <ArrowRight className="size-3.5" />
            </Button>
          ) : (
            <Button
              type="button"
              isPending={creating}
              isDisabled={creating}
              className={"shadow-md dark:shadow-none shadow-accent font-bold items-center h-12.5"}
              fullWidth
              onClick={onCreateClick}
            >
              {creating ? (
                "Creating..."
              ) : (
                <>
                  <Check className="size-3.5" />
                  Create
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateMeetsComp;